// src/contexts/AuthContext.js
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';

import { auth, db } from '../firebase';

const AuthContext = createContext(null);

/* ------------------ Helpers ------------------ */

function getRoleCollection(role) {
  if (role === 'customer') return 'customers';
  if (role === 'tasker') return 'taskers';
  if (role === 'admin') return 'admins';
  return null;
}

async function ensureRoleDoc({ uid, role, email }) {
  const collection = getRoleCollection(role);
  if (!collection) return;

  const ref = doc(db, collection, uid);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    await setDoc(ref, { email: email || '' }, { merge: true });
  }
}

async function getUserProfile(uid) {
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);

  if (!userSnap.exists()) return null;

  const userData = userSnap.data();
  const roleCollection = getRoleCollection(userData.role);

  if (!roleCollection) {
    return { uid, ...userData, roleProfile: null };
  }

  const roleRef = doc(db, roleCollection, uid);

  const [roleSnap] = await Promise.all([
    getDoc(roleRef),
  ]);

  return {
    uid,
    ...userData,
    roleProfile: roleSnap.exists() ? roleSnap.data() : null,
  };
}

/* ------------------ Provider ------------------ */

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (!firebaseUser) {
        setProfile(null);
        setLoading(false);
        return;
      }

      try {
        const profileData = await getUserProfile(firebaseUser.uid);

        if (!profileData) {
          await signOut(auth);
          setProfile(null);
          return;
        }

        if (profileData.status === 'suspended') {
          await signOut(auth);
          setProfile(null);
          return;
        }

        await ensureRoleDoc({
          uid: firebaseUser.uid,
          role: profileData.role,
          email: firebaseUser.email,
        });

        setProfile(profileData);
      } catch (err) {
        console.error('Auth init error:', err);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  /* ------------------ Actions ------------------ */

  const signup = async ({ email, password, role }) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    const uid = cred.user.uid;
    await sendEmailVerification(cred.user);

    await setDoc(
      doc(db, 'users', uid),
      {
        role,
        status: 'active',
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );

    await ensureRoleDoc({ uid, role, email });

    const profileData = await getUserProfile(uid);
    setUser(cred.user);
    setProfile(profileData);

    return cred.user;
  };

  const login = async ({ email, password }) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const profileData = await getUserProfile(cred.user.uid);

    if (!profileData) {
      await signOut(auth);
      throw new Error('No user profile found.');
    }

    if (profileData.status === 'suspended') {
      await signOut(auth);
      throw new Error('Account is suspended.');
    }

    await ensureRoleDoc({
      uid: cred.user.uid,
      role: profileData.role,
      email: cred.user.email,
    });

    setProfile(profileData);
    return { user: cred.user, profile: profileData };
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setProfile(null);
  };

  /* ------------------ Context Value ------------------ */

  const value = useMemo(
    () => ({
      user,
      profile,
      role: profile?.role || null,
      status: profile?.status || null,
      loading,
      signup,
      login,
      logout,
    }),
    [user, profile, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ------------------ Hook ------------------ */

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
